import React, { useEffect, useState } from 'react';
import { Card, Typography, Radio, RadioGroup, FormControl, FormControlLabel, TextField, Button, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Enrollment } from './models/Enrollment';
import TopNavActions from './TopNavActions';
import ConfirmExitDialog from './ConfirmExitDialog';

interface Option {
  id: string;
  name: string;
}

interface Props {
  enrollment: Enrollment;
  onPageChange: (page: string) => void;
}

const learnAboutThisStudyOptions: Option[] = [
  { id: 'physician', name: 'Through my physician/provider' },
  { id: 'flyer', name: 'A flyer' },
  { id: 'social_media', name: 'Social Media' },
  { id: 'bridge2ai_website', name: 'Bridge2AI Website' },
  { id: 'event', name: 'At an event' },
  { id: 'other', name: 'Other' },
];

const HowDidYouLearnAboutThisStudyView: React.FC<Props> = ({ enrollment, onPageChange }) => {
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [eventSpecified, setEventSpecified] = useState('');
  const [otherSpecified, setOtherSpecified] = useState('');
  const [openExitDialog, setOpenExitDialog] = React.useState(false);
  const { t } = useTranslation('enrollment');

  useEffect(() => {
    setSelectedOption(enrollment.learnAboutThisStudy ? [enrollment.learnAboutThisStudy] : []);
    setEventSpecified(enrollment.learnAboutThisStudyEventSpecified ?? '');
    setOtherSpecified(enrollment.learnAboutThisStudyOtherSpecified ?? '');
    // eslint-disable-next-line
  }, []);

  const handleOptionChange = (id: string) => {
    setSelectedOption([id]);
    if (id !== 'event') setEventSpecified('');
    if (id !== 'other') setOtherSpecified('');
  };

  const handleNext = () => {
    enrollment.learnAboutThisStudy = selectedOption[0];
    enrollment.learnAboutThisStudyEventSpecified = selectedOption.includes('event') ? eventSpecified : '';
    enrollment.learnAboutThisStudyOtherSpecified = selectedOption.includes('other') ? otherSpecified : '';
    onPageChange('contact-information-view');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const isNextDisabled =
    selectedOption.length === 0 ||
    (selectedOption.includes('event') && eventSpecified.trim() === '') ||
    (selectedOption.includes('other') && otherSpecified.trim() === '');

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />

      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1} sx={{mb: 2}}>{t("How did you learn about this study?")}</Typography>

      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{t("Please select one of the following options:")}</FormLabel>
          <RadioGroup
            name="learnAboutThisStudy"
            value={selectedOption[0] || ''}
            onChange={e => handleOptionChange(e.target.value)}
          >
            {learnAboutThisStudyOptions.map(option => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio />}
                label={t(option.name)}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {selectedOption.includes('event') && (
          <TextField
            fullWidth
            label={`${t("At an event")}, ${t("Please specify")}:`}
            value={eventSpecified}
            onChange={e => setEventSpecified(e.target.value)}
            margin='normal'
          />

        )}
        {selectedOption.includes('other') && (
          <TextField
            fullWidth
            label={`${t("Other")}, ${t("Please specify")}:`}
            value={otherSpecified}
            onChange={e => setOtherSpecified(e.target.value)}
            margin='normal'
          />
        )}
       </Card>
      <Button variant="contained"
        onClick={handleNext}
        disabled={isNextDisabled}
        style={{ marginTop: 32, marginBottom: 32 }}
      >
        {t("Next")}
      </Button>
    </React.Fragment>
  );
};

export default HowDidYouLearnAboutThisStudyView;
